/**
 * Pure navigation / shell helpers for the TinyBase console.
 * Kept free of React so unit tests can exercise the real shipped logic.
 */

/** Tailwind-aligned shell breakpoints (px). */
export const SHELL_BREAKPOINTS = {
  /** Platform sidebar becomes persistent */
  platformSidebar: 768,
  /** Project sidebar becomes persistent; below this use sheet + strip */
  projectSidebar: 1024,
  /** Reference mobile width for responsive checks */
  mobile: 375,
  /** Narrow phone reference (iPhone SE class) */
  mobileNarrow: 320,
} as const;

/** DESIGN.md desktop sidebar width: 264px / 16px. */
export const SIDEBAR_WIDTH_REM = 16.5;
export const SIDEBAR_COLLAPSED_REM = 4;

/** DESIGN.md mobile drawer: min(88vw, 360px). */
export const MOBILE_DRAWER_MAX_PX = 360;

/** Preferred mobile touch target (px); DESIGN.md §15.4. */
export const MOBILE_TOUCH_TARGET_PX = 44;

/** Standard control minimum (px). */
export const STANDARD_TOUCH_TARGET_PX = 40;

export type ProjectNavLeaf = {
  href: string;
  label: string;
  hint: string;
};

export type ProjectNavItem = ProjectNavLeaf & {
  children?: ProjectNavLeaf[];
};

export type ProjectNavGroup = {
  label: string;
  description: string;
  items: ProjectNavItem[];
};

/**
 * Canonical project navigation tree (labels + routes).
 * Icons stay in ProjectShell; this is the single source of path/label truth.
 */
export const PROJECT_NAV_GROUPS: ProjectNavGroup[] = [
  {
    label: "Build",
    description: "Data, queries, and live services",
    items: [
      { href: "", label: "Overview", hint: "Keys & health" },
      { href: "/tables", label: "Tables", hint: "Schema & rows" },
      { href: "/sql", label: "SQL", hint: "Query & migrate" },
      {
        href: "/database",
        label: "Database",
        hint: "Cluster, backup & lifecycle",
        children: [
          {
            href: "/database/schemas",
            label: "Schema Visualizer",
            hint: "Explore tables & relationships",
          },
          {
            href: "/database/tables",
            label: "Tables",
            hint: "Browse columns & rows",
          },
          {
            href: "/database/functions",
            label: "Database Functions",
            hint: "PostgreSQL routines",
          },
          {
            href: "/database/triggers",
            label: "Database Triggers",
            hint: "Automated database actions",
          },
        ],
      },
      { href: "/storage", label: "Storage", hint: "Objects & images" },
      { href: "/realtime", label: "Realtime", hint: "Live change feed" },
      {
        href: "/functions",
        label: "Edge Functions",
        hint: "Deploy & observe",
      },
    ],
  },
  {
    label: "Access",
    description: "Identity and authorization",
    items: [
      { href: "/permissions", label: "Permissions", hint: "RLS policies" },
      {
        href: "/advisors",
        label: "Advisors",
        hint: "Security checks",
        children: [
          {
            href: "/advisors/security",
            label: "Security",
            hint: "RLS findings",
          },
        ],
      },
      { href: "/auth", label: "Auth", hint: "Users, sessions, schema" },
      {
        href: "/applications",
        label: "Applications",
        hint: "Hosted auth clients",
      },
    ],
  },
  {
    label: "Operate",
    description: "Usage, health, and diagnostics",
    items: [
      { href: "/usage", label: "Usage", hint: "Hard quotas" },
      {
        href: "/observability",
        label: "Observability",
        hint: "Service health",
        children: [
          {
            href: "/observability/gateway",
            label: "API Gateway",
            hint: "Traffic & errors",
          },
          {
            href: "/observability/database",
            label: "Database",
            hint: "Storage & pool",
          },
          {
            href: "/observability/logs",
            label: "Logs",
            hint: "Search events",
          },
          {
            href: "/observability/auth",
            label: "Auth report",
            hint: "Sign-in metrics",
          },
          {
            href: "/observability/mcp",
            label: "MCP",
            hint: "Agent tool calls",
          },
          {
            href: "/observability/alerts",
            label: "Alerts",
            hint: "Rules & incidents",
          },
          {
            href: "/observability/reports",
            label: "Reports",
            hint: "Custom layouts",
          },
        ],
      },
    ],
  },
];

/** Flat primary (top-level) project destinations for the mobile strip. */
export function listProjectPrimaryNavItems(): ProjectNavLeaf[] {
  return PROJECT_NAV_GROUPS.flatMap((group) =>
    group.items.map(({ href, label, hint }) => ({ href, label, hint })),
  );
}

/** Flatten primary + nested leaves (depth-first). */
export function listProjectNavLeaves(): ProjectNavLeaf[] {
  const out: ProjectNavLeaf[] = [];
  for (const group of PROJECT_NAV_GROUPS) {
    for (const item of group.items) {
      out.push({ href: item.href, label: item.label, hint: item.hint });
      if (item.children) {
        for (const child of item.children) {
          out.push(child);
        }
      }
    }
  }
  return out;
}

/**
 * Whether a project-shell nav item is active for the current pathname.
 * Overview (href "") is exact match only; /observability is exact (not nested).
 */
export function isProjectNavActive(
  pathname: string,
  base: string,
  href: string,
): boolean {
  if (href === "") return pathname === base || pathname === `${base}/`;
  if (href === "/observability") {
    return (
      pathname === `${base}/observability` ||
      pathname === `${base}/observability/`
    );
  }
  return (
    pathname === `${base}${href}` ||
    pathname.startsWith(`${base}${href}/`)
  );
}

/**
 * True when a parent with children should show its nested list
 * (parent active or any child active). Keeps mobile drawers scannable.
 */
export function isProjectNavBranchExpanded(
  pathname: string,
  base: string,
  item: ProjectNavItem,
): boolean {
  if (!item.children?.length) return false;
  if (isProjectNavActive(pathname, base, item.href)) return true;
  return item.children.some((child) =>
    isProjectNavActive(pathname, base, child.href),
  );
}

/**
 * Human-readable page label for the current project route (header/mobile).
 * Prefers the most specific matching leaf (child over parent).
 */
export function resolveProjectNavLabel(
  pathname: string,
  base: string,
): string {
  let best: { href: string; label: string } | null = null;
  for (const leaf of listProjectNavLeaves()) {
    if (!isProjectNavActive(pathname, base, leaf.href)) continue;
    if (!best || leaf.href.length >= best.href.length) {
      best = { href: leaf.href, label: leaf.label };
    }
  }
  return best?.label ?? "Project";
}

/** True when the persistent project sidebar should be shown (desktop). */
export function prefersProjectSidebar(viewportWidth: number): boolean {
  return viewportWidth >= SHELL_BREAKPOINTS.projectSidebar;
}

/** True when the persistent platform sidebar should be shown. */
export function prefersPlatformSidebar(viewportWidth: number): boolean {
  return viewportWidth >= SHELL_BREAKPOINTS.platformSidebar;
}

/** True when the mobile project quick-nav strip should render. */
export function prefersProjectNavStrip(viewportWidth: number): boolean {
  return viewportWidth < SHELL_BREAKPOINTS.projectSidebar;
}

/**
 * Whether primary navigation remains reachable at a given width.
 * Desktop: persistent sidebar; mobile: sheet and/or horizontal strip.
 */
export function isPrimaryNavReachable(
  viewportWidth: number,
  opts: { hasMobileSheet: boolean; hasNavStrip?: boolean; hasChipStrip?: boolean },
): boolean {
  if (viewportWidth >= SHELL_BREAKPOINTS.projectSidebar) return true;
  if (viewportWidth >= SHELL_BREAKPOINTS.platformSidebar) {
    // Platform shell only needs md+ sidebar or a mobile sheet
    return true;
  }
  return (
    opts.hasMobileSheet ||
    Boolean(opts.hasNavStrip) ||
    Boolean(opts.hasChipStrip)
  );
}

/**
 * Compact statusline: on narrow viewports keep only the first N items
 * (essential identity) so secondary metadata does not crowd the header.
 */
export function statuslineVisibleCount(viewportWidth: number): number {
  if (viewportWidth < SHELL_BREAKPOINTS.mobile) return 2;
  if (viewportWidth < SHELL_BREAKPOINTS.platformSidebar) return 3;
  return Number.POSITIVE_INFINITY;
}

/** CSS width contract for the mobile navigation drawer. */
export function mobileDrawerWidthCss(
  maxPx: number = MOBILE_DRAWER_MAX_PX,
): string {
  return `min(88vw, ${maxPx}px)`;
}

/** Prevent whole-page horizontal overflow on core shells (CSS class contract). */
export const SHELL_OVERFLOW_CLASS = "min-w-0 overflow-x-hidden";
